import { Box, Divider, Stack, Typography } from '@mui/material';
import { radius } from '@karehero/llama/theme/radius';

import { BENEFITS } from '@/benefits-flow/engine/benefits';
import { evaluate } from '@/benefits-flow/engine/evaluate';
import Pill from '@/benefits-flow/Pill';
import {
  type Answers,
  QUESTIONS,
  QUIZ_ORDER,
  buildInput,
} from '@/benefits-flow/screener/questions';
import { ACTION_TONE } from '@/benefits-flow/tones';
import {
  type AnswerRoute,
  type Reliability,
  GROUPS,
  LEDGER,
  PEOPLE,
  RELIABILITY_META,
  ROUTES,
  ROUTE_META,
  ROUTING,
  SHORT_LABEL,
  TODAY_SECTION,
  TODAY,
} from '@/karehero-app/explore/benefits-calculator/BenefitsCalculator.content';

// Section A: the questions as the source of truth, before any mode is argued
// about. Two frames, one job each — the proposed grouping, and the grouping as it
// stands today.
//
// House rule for the whole exploration: no count and no pound figure is written
// into the copy. Everything is computed here from the ported screener and engine,
// so a frame cannot claim something the engine disagrees with. That rule is what
// caught the payoff frame asserting a win the engine did not produce.

// Two scales live side by side in this table, so only one of them gets to use
// colour. Reliability is the one worth scanning down a column, so it keeps the
// colour; the channels are a set of categories with no ordering, so they are all
// one outlined treatment and the label does the work.
//
// Nothing here is grey. A grey chip reads as disabled, and every one of these is
// a live option — a greyed-out "Tap" said the opposite of what it meant.
const CHANNEL_TONE = {
  bg: 'background.paper',
  color: 'custom.charcoal',
  border: 'custom.neutral.200',
};

const RELIABILITY_TONE = {
  lime: { bg: 'custom.limeGreen.100', color: 'custom.charcoal' },
  sun: { bg: 'sunflower.main', color: 'custom.charcoal' },
  // Solid rather than a tint: "never infer" is a hard line, and it has to be
  // impossible to mistake for one of the outlined channel chips beside it.
  stop: { bg: 'custom.charcoal', color: 'common.white' },
};

// Legend order: the channel we want to lead with first, and the one that only
// exists because a conversation must not be trusted with it, last.
const CHANNEL_ORDER: AnswerRoute[] = ['waffle', 'document', 'typed', 'tap'];

const RoutePill = ({ route }: { route: AnswerRoute }) => (
  <Pill label={ROUTE_META[route].short} tone={CHANNEL_TONE} />
);

const ReliabilityPill = ({ reliability }: { reliability: Reliability }) => (
  <Pill
    label={RELIABILITY_META[reliability].label}
    tone={RELIABILITY_TONE[RELIABILITY_META[reliability].tone]}
  />
);

const Lead = ({ text }: { text: string }) => (
  <Typography sx={{ maxWidth: 680 }} variant='h4'>
    {text}
  </Typography>
);

const Body = ({ text }: { text: string }) => (
  <Typography sx={{ maxWidth: 760 }} variant='body2' color='text.secondary'>
    {text}
  </Typography>
);

const Panel = ({ children }: { children: React.ReactNode }) => (
  <Stack
    sx={{
      p: 3,
      borderRadius: `${radius.md}px`,
      border: 1,
      borderColor: 'custom.neutral.200',
      bgcolor: 'background.paper',
    }}
  >
    {children}
  </Stack>
);

const eligibleNames = (answers: Answers): string[] =>
  evaluate(buildInput(answers, QUIZ_ORDER)).eligibleIds.map(
    (id) => BENEFITS[id].shortName,
  );

// ── A1. The ledger, grouped and ordered ──────────────────────────────────────

// Which benefits a question is load-bearing for: drop it from a fully answered
// person and see what stops resolving. Computed per person, so it stays honest
// about being one worked example rather than a general truth.
const loadBearing = (id: string, answers: Answers): string[] => {
  const full = eligibleNames(answers);
  const without = { ...answers };
  delete without[id];
  const remaining = eligibleNames(without);
  return full.filter((name) => !remaining.includes(name));
};

const LedgerRow = ({ id }: { id: string }) => {
  const entry = LEDGER[id];
  const routing = ROUTES[id];
  const unlocks = loadBearing(id, PEOPLE[0].answers);
  const section = TODAY_SECTION[id];
  // The screener already authors a plain-English condition per conditional
  // question, so this is read rather than invented. Eight of the nineteen have
  // none — they are always asked.
  const gate = QUESTIONS[id].gate;

  return (
    <Stack
      sx={{ alignItems: 'flex-start', py: 1.75 }}
      direction='row'
      spacing={3}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
        <Typography variant='subtitle2'>{SHORT_LABEL[id]}</Typography>
        {gate && (
          <Typography variant='caption' color='custom.coral.700'>
            {ROUTING.gatePrefix} {gate.replace(/^if /, '')}
          </Typography>
        )}
        <Typography variant='caption' color='text.secondary'>
          {entry.basis}
        </Typography>
        {unlocks.length > 0 && (
          <Typography variant='caption' color='custom.coral.700'>
            {ROUTING.unlocksPrefix} {unlocks.join(', ')}
          </Typography>
        )}
      </Stack>

      {/* Where it sits in today's grouping, so the regroup reads as a
          reshuffle of a real thing rather than a fresh invention. */}
      <Typography
        sx={{ width: 140, flexShrink: 0 }}
        variant='caption'
        color='text.disabled'
      >
        {section ? `${ROUTING.todayPrefix} ${section}` : ROUTING.notAskedToday}
      </Typography>

      <Stack sx={{ width: 110, flexShrink: 0, alignItems: 'flex-start' }}>
        <ReliabilityPill reliability={entry.reliability} />
      </Stack>

      <Stack
        sx={{ width: 130, flexShrink: 0, alignItems: 'flex-start' }}
        spacing={0.5}
      >
        {routing.routes.map((route) => (
          <RoutePill key={route} route={route} />
        ))}
      </Stack>

      <Typography
        sx={{ width: 64, flexShrink: 0 }}
        variant='caption'
        color='text.disabled'
      >
        {entry.decay === 'stable' ? 'Stable' : 'Goes off'}
      </Typography>
    </Stack>
  );
};

const LedgerGroup = ({
  index,
  title,
  intent,
  ids,
}: {
  index: number;
  title: string;
  intent: string;
  ids: string[];
}) => (
  <Panel>
    <Stack spacing={2}>
      <Stack sx={{ alignItems: 'baseline' }} direction='row' spacing={2}>
        <Typography variant='h2' color='custom.neutral.300'>
          {index}
        </Typography>
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
          <Typography variant='h3'>{title}</Typography>
          <Typography variant='body2' color='text.secondary'>
            {intent}
          </Typography>
        </Stack>
      </Stack>
      <Stack divider={<Divider />}>
        {ids.map((id) => (
          <LedgerRow key={id} id={id} />
        ))}
      </Stack>
    </Stack>
  </Panel>
);

export const A1Ledger = () => (
  <Stack spacing={3}>
    <Lead text={ROUTING.title} />
    <Body text={ROUTING.subtitle} />

    <Stack sx={{ alignItems: 'stretch' }} direction='row' spacing={2}>
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Panel>
          <Stack spacing={1.5}>
            <Typography variant='overline' color='text.secondary'>
              {ROUTING.reliabilityLegend}
            </Typography>
            {(['firm', 'hedged', 'never'] as Reliability[]).map(
              (reliability) => (
                <Stack
                  sx={{ alignItems: 'flex-start' }}
                  key={reliability}
                  direction='row'
                  spacing={2}
                >
                  <Box sx={{ width: 110, flexShrink: 0, display: 'flex' }}>
                    <ReliabilityPill reliability={reliability} />
                  </Box>
                  <Typography
                    sx={{ flex: 1, minWidth: 0 }}
                    variant='body2'
                    color='text.secondary'
                  >
                    {RELIABILITY_META[reliability].meaning}
                  </Typography>
                </Stack>
              ),
            )}
          </Stack>
        </Panel>
      </Stack>

      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Panel>
          <Stack spacing={1.5}>
            <Typography variant='overline' color='text.secondary'>
              {ROUTING.channelLegend}
            </Typography>
            {CHANNEL_ORDER.map((route) => (
              <Stack
                sx={{ alignItems: 'center' }}
                key={route}
                direction='row'
                spacing={2}
              >
                <Box sx={{ width: 110, flexShrink: 0, display: 'flex' }}>
                  <RoutePill route={route} />
                </Box>
                <Typography
                  sx={{ flex: 1, minWidth: 0 }}
                  variant='body2'
                  color='text.secondary'
                >
                  {ROUTE_META[route].label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Panel>
      </Stack>
    </Stack>

    <Stack spacing={2}>
      {GROUPS.map((group, i) => (
        <LedgerGroup
          key={group.id}
          index={i + 1}
          title={group.title}
          intent={group.intent}
          // Questions in this group, kept in the order the screener asks them.
          ids={QUIZ_ORDER.filter((id) => LEDGER[id]?.group === group.id)}
        />
      ))}
    </Stack>

    <Body text={ROUTING.footnote} />
  </Stack>
);
A1Ledger.displayName = 'A1Ledger';

// ── A2. What that order is today ─────────────────────────────────────────────

export const A2Today = () => (
  <Stack spacing={3}>
    <Lead text={TODAY.title} />
    <Body
      text={TODAY.subtitle(
        TODAY.groups.length,
        TODAY.groups.reduce((n, group) => n + group.questions.length, 0),
      )}
    />

    <Stack sx={{ alignItems: 'stretch' }} direction='row' spacing={2}>
      {TODAY.groups.map((group, i) => (
        <Stack sx={{ flex: 1, minWidth: 0 }} key={group.title}>
          <Panel>
            <Stack spacing={1.5}>
              <Stack spacing={0.25}>
                <Typography variant='caption' color='text.disabled'>
                  {i + 1}
                </Typography>
                <Typography variant='subtitle2'>{group.title}</Typography>
              </Stack>
              <Divider />
              <Stack spacing={1}>
                {group.questions.map((question) => (
                  <Stack key={question.label} spacing={0.5}>
                    {/* One question in the set is required. The frame's brief
                        beside the sketch is the asterisk's footnote, so a chip or
                        a sentence per row would be furniture. */}
                    <Typography variant='body2'>
                      {question.label}
                      {question.required && (
                        <Box
                          sx={{ color: 'custom.coral.600' }}
                          component='span'
                        >
                          {' *'}
                        </Box>
                      )}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Panel>
        </Stack>
      ))}
    </Stack>
  </Stack>
);
A2Today.displayName = 'A2Today';
